import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  DollarSign, ShoppingCart, Users, Package, TrendingUp, TrendingDown,
  ArrowUpRight, AlertTriangle, PackageX, RefreshCw, Boxes
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const money = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const compact = (n) => (n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n || 0)}`);

const STATUS_META = {
  pending: { label: 'Pending', color: '#F59E0B' },
  processing: { label: 'Processing', color: '#3B82F6' },
  ready_to_ship: { label: 'Ready to Ship', color: '#6366F1' },
  shipped: { label: 'Shipped', color: '#8B5CF6' },
  in_transit: { label: 'In Transit', color: '#8B5CF6' },
  delivered: { label: 'Delivered', color: '#10B981' },
  completed: { label: 'Completed', color: '#10B981' },
  cancelled: { label: 'Cancelled', color: '#EF4444' },
  unknown: { label: 'Other', color: '#64748B' },
};
const statusMeta = (s) => STATUS_META[s] || { label: s.charAt(0).toUpperCase() + s.slice(1), color: '#64748B' };

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const Card = ({ className = '', children, testid, onClick }) => (
  <div data-testid={testid} onClick={onClick} className={`bg-[#14171B] border border-white/[0.06] rounded-2xl ${className} ${onClick ? 'cursor-pointer hover:border-[#C6A15B]/30 transition-colors' : ''}`}>{children}</div>
);

const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

const ChangeBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? 'text-emerald-400' : 'text-red-400'}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
      {up ? '+' : ''}{pct}% <span className="text-slate-500 font-normal">vs last month</span>
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, change, accent, testid, onClick }) => (
  <Card className="p-5" testid={testid} onClick={onClick}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">{label}</p>
        <p className="text-white text-[28px] leading-none font-semibold tracking-tight">{value}</p>
        {sub && <p className="text-slate-500 text-xs mt-2">{sub}</p>}
        <div className="mt-2"><ChangeBadge pct={change} /></div>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

// Lightweight responsive SVG area chart with interactive hover tooltip
const RevenueAreaChart = ({ series }) => {
  const W = 700, H = 240, PADX = 16, PADB = 28, PADT = 16;
  const data = series || [];
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const n = data.length;
  const xFor = (i) => PADX + (n <= 1 ? 0 : (i / (n - 1)) * (W - PADX * 2));
  const yFor = (v) => PADT + (1 - v / max) * (H - PADT - PADB);
  const pts = data.map((d, i) => [xFor(i), yFor(d.revenue)]);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = pts.length ? `${line} L${xFor(n - 1).toFixed(1)},${H - PADB} L${xFor(0).toFixed(1)},${H - PADB} Z` : '';
  const hasData = data.some((d) => d.revenue > 0);

  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // index

  // Fractions (0..1) used for responsive HTML overlay positioning (svg is stretched to fill).
  const xFrac = (i) => xFor(i) / W;
  const yFrac = (i) => yFor(data[i].revenue) / H;

  const pickIndex = (clientX) => {
    const el = wrapRef.current;
    if (!el || n === 0) return null;
    const rect = el.getBoundingClientRect();
    const rel = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    let best = 0, bestD = Infinity;
    for (let i = 0; i < n; i++) {
      const d = Math.abs(xFrac(i) - rel);
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  };
  const onMove = (e) => setHover(pickIndex(e.clientX));
  const onTouch = (e) => { if (e.touches && e.touches[0]) setHover(pickIndex(e.touches[0].clientX)); };
  const onLeave = () => setHover(null);

  // Tooltip horizontal anchoring: clamp so it never overflows the chart edges.
  const tipTransform = (xf) => (xf < 0.14 ? 'translate(0, -115%)' : xf > 0.86 ? 'translate(-100%, -115%)' : 'translate(-50%, -115%)');

  return (
    <div
      ref={wrapRef}
      className="w-full relative"
      data-testid="revenue-chart"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onTouchStart={onTouch}
      onTouchMove={onTouch}
      onTouchEnd={onLeave}
    >
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[240px]" preserveAspectRatio="none">
        <defs>
          <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C6A15B" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#C6A15B" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((g, i) => (
          <line key={i} x1={PADX} x2={W - PADX} y1={PADT + g * (H - PADT - PADB)} y2={PADT + g * (H - PADT - PADB)}
            stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1" />
        ))}
        {hasData && <path d={area} fill="url(#goldFill)" />}
        {hasData && <path d={line} fill="none" stroke="#C6A15B" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}
        {hasData && hover !== null && (
          <line x1={xFor(hover)} x2={xFor(hover)} y1={PADT} y2={H - PADB}
            stroke="#C6A15B" strokeOpacity="0.35" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
        )}
        {hasData && pts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r={hover === i ? 5 : 3.5}
            fill={hover === i ? '#C6A15B' : '#0C0E11'} stroke="#C6A15B" strokeWidth="2" />
        ))}
        {data.map((d, i) => {
          const many = n > 12;
          const step = many ? Math.ceil(n / 6) : 1;
          if (many && i % step !== 0 && i !== n - 1) return null;
          const label = many ? d.label : d.label.split(' ')[0];
          return (
            <text key={i} x={xFor(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="#64748B">{label}</text>
          );
        })}
      </svg>

      {hasData && hover !== null && (
        <div
          className="pointer-events-none absolute z-20 rounded-lg border border-[#C6A15B]/30 bg-[#0C0E11]/95 px-3 py-2 shadow-xl shadow-black/40 backdrop-blur-sm whitespace-nowrap"
          style={{ left: `${xFrac(hover) * 100}%`, top: `${yFrac(hover) * 100}%`, transform: tipTransform(xFrac(hover)) }}
          data-testid="revenue-tooltip"
        >
          <p className="text-slate-400 text-[11px] leading-none mb-1">{data[hover].label}</p>
          <p className="text-slate-500 text-[10px] uppercase tracking-wider leading-none mb-1">Revenue</p>
          <p className="text-white text-sm font-semibold leading-none" data-testid="revenue-tooltip-amount">{money(data[hover].revenue)}</p>
        </div>
      )}

      {!hasData && <p className="text-center text-slate-500 text-sm -mt-28 relative z-10 pb-24">No revenue recorded for this period.</p>}
    </div>
  );
};

// SVG donut for order status
const StatusDonut = ({ breakdown }) => {
  const entries = Object.entries(breakdown || {}).filter(([, v]) => v > 0);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  const R = 52, C = 2 * Math.PI * R;
  let offset = 0;
  const segs = entries.map(([k, v]) => {
    const frac = total ? v / total : 0;
    const seg = { k, v, color: statusMeta(k).color, dash: frac * C, offset };
    offset += frac * C;
    return seg;
  });
  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <div className="relative shrink-0">
        <svg width="140" height="140" viewBox="0 0 140 140">
          <circle cx="70" cy="70" r={R} fill="none" stroke="#ffffff" strokeOpacity="0.06" strokeWidth="14" />
          {total > 0 && segs.map((s) => (
            <circle key={s.k} cx="70" cy="70" r={R} fill="none" stroke={s.color} strokeWidth="14"
              strokeDasharray={`${s.dash} ${C - s.dash}`} strokeDashoffset={-s.offset}
              transform="rotate(-90 70 70)" strokeLinecap="butt" />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-white text-2xl font-semibold">{total}</span>
          <span className="text-slate-500 text-[11px] uppercase tracking-wider">Orders</span>
        </div>
      </div>
      <div className="flex-1 w-full space-y-2">
        {entries.length === 0 && <p className="text-slate-500 text-sm">No orders yet.</p>}
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-slate-300">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: statusMeta(k).color }} />
              {statusMeta(k).label}
            </span>
            <span className="text-white font-medium">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-white font-semibold">{children}</h3>
    {action}
  </div>
);

const ViewLink = ({ onClick, children }) => (
  <button onClick={onClick} className="inline-flex items-center gap-1 text-xs text-[#C6A15B] hover:text-[#E7CB85] transition-colors">
    {children} <ArrowUpRight className="w-3.5 h-3.5" />
  </button>
);

export const ExecutiveDashboard = ({ user, setActiveTab }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState('6m');

  const load = async (p = period) => {
    setLoading(true); setError(false);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/dashboard?period=${p}`);
      setData(res.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period]);

  const k = data?.kpis || {};
  const periods = [{ k: '30d', l: '30 Days' }, { k: '3m', l: '3 Months' }, { k: '6m', l: '6 Months' }, { k: '12m', l: '12 Months' }];
  const goInventory = (f) => {
    try { if (f) sessionStorage.setItem('tc_products_filter', f); else sessionStorage.removeItem('tc_products_filter'); } catch (e) { /* ignore */ }
    setActiveTab('products');
  };

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#14171B] via-[#111418] to-[#0C0E11] px-6 py-6">
        <div className="absolute -right-10 -top-16 w-72 h-72 rounded-full bg-[#C6A15B]/10 blur-3xl pointer-events-none" />
        <p className="text-slate-400 text-sm">{greeting()},</p>
        <h2 className="text-2xl sm:text-3xl font-semibold text-white mt-0.5">{user?.full_name || 'TC Administrator'}</h2>
        <p className="text-slate-500 text-sm mt-1">Here's what's happening with TC today.</p>
      </div>

      {error && (
        <Card className="p-5 flex items-center justify-between" testid="dashboard-error">
          <span className="text-red-300 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Unable to load dashboard data.</span>
          <button onClick={() => load()} className="inline-flex items-center gap-1.5 text-xs bg-white/[0.05] hover:bg-white/[0.1] text-white px-3 py-1.5 rounded-lg">
            <RefreshCw className="w-3.5 h-3.5" /> Retry
          </button>
        </Card>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {loading ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[132px]" />) : (
          <>
            <KpiCard testid="kpi-revenue" icon={DollarSign} label="Total Revenue" value={money(k.total_revenue)}
              change={k.revenue_change_pct} accent="bg-[#C6A15B]/15 text-[#C6A15B]" />
            <KpiCard testid="kpi-orders" icon={ShoppingCart} label="Total Orders" value={k.total_orders}
              change={k.orders_change_pct} accent="bg-blue-500/15 text-blue-400" />
            <KpiCard testid="kpi-customers" icon={Users} label="Active Customers" value={k.active_customers}
              accent="bg-emerald-500/15 text-emerald-400" />
            <KpiCard testid="kpi-products" icon={Package} label="Products" value={k.active_products}
              sub={k.out_of_stock_count > 0 ? `${k.out_of_stock_count} out of stock` : (k.low_stock_count > 0 ? `${k.low_stock_count} low in stock` : 'All in stock')}
              accent="bg-purple-500/15 text-purple-400"
              onClick={() => goInventory(k.out_of_stock_count > 0 ? 'out' : (k.low_stock_count > 0 ? 'low' : null))} />
          </>
        )}
      </div>

      {/* Row 2: revenue + status + inventory */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="p-5 lg:col-span-6" testid="revenue-chart-card">
          <SectionTitle action={
            <div className="flex gap-1 bg-black/30 rounded-lg p-0.5">
              {periods.map((p) => (
                <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`period-${p.k}`}
                  className={`px-2.5 py-1 rounded-md text-xs transition-colors ${period === p.k ? 'bg-[#C6A15B] text-black font-medium' : 'text-slate-400 hover:text-white'}`}>
                  {p.l}
                </button>
              ))}
            </div>
          }>Revenue Overview</SectionTitle>
          {loading ? <Skeleton className="h-[240px]" /> : <RevenueAreaChart series={data?.revenue_series} />}
        </Card>

        <Card className="p-5 lg:col-span-3" testid="status-card">
          <SectionTitle action={<ViewLink onClick={() => setActiveTab('orders')}>View all</ViewLink>}>Order Status</SectionTitle>
          {loading ? <Skeleton className="h-[160px]" /> : <StatusDonut breakdown={data?.status_breakdown} />}
        </Card>

        <Card className="p-5 lg:col-span-3" testid="inventory-card">
          <SectionTitle action={<ViewLink onClick={() => goInventory()}>View inventory</ViewLink>}>Inventory Alerts</SectionTitle>
          {loading ? <Skeleton className="h-[160px]" /> : (
            (data?.inventory_alerts?.length ? (
              <div className="space-y-2.5">
                {data.inventory_alerts.map((a) => (
                  <button key={a.product_id} type="button" onClick={() => goInventory(a.status === 'out_of_stock' ? 'out' : 'low')}
                    data-testid={`inventory-alert-${a.product_id}`}
                    className="w-full flex items-center justify-between gap-2 text-left rounded-lg hover:bg-white/[0.03] px-1.5 py-1 -mx-1.5 transition-colors">
                    <div className="min-w-0">
                      <p className="text-slate-200 text-sm truncate">{a.name}</p>
                      <p className="text-slate-500 text-xs">{a.stock} units</p>
                    </div>
                    <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      a.status === 'out_of_stock' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'}`}>
                      {a.status === 'out_of_stock' ? <PackageX className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                      {a.status === 'out_of_stock' ? 'Out' : 'Low'}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-6">
                <Boxes className="w-8 h-8 text-slate-600 mb-2" />
                <p className="text-slate-400 text-sm">No inventory alerts</p>
                <p className="text-slate-600 text-xs mt-1">All products are above the low-stock threshold.</p>
              </div>
            ))
          )}
        </Card>
      </div>

      {/* Row 3: recent orders + top products */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="p-5 lg:col-span-7" testid="recent-orders-card">
          <SectionTitle action={<ViewLink onClick={() => setActiveTab('orders')}>View all</ViewLink>}>Recent Orders</SectionTitle>
          {loading ? <Skeleton className="h-[220px]" /> : (
            data?.recent_orders?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-500 text-xs border-b border-white/[0.06]">
                      <th className="text-left font-medium py-2">Order #</th>
                      <th className="text-left font-medium py-2">Customer</th>
                      <th className="text-left font-medium py-2 hidden sm:table-cell">Date</th>
                      <th className="text-right font-medium py-2">Total</th>
                      <th className="text-right font-medium py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_orders.map((o) => {
                      const m = statusMeta(o.status);
                      return (
                        <tr key={o.order_id} className="border-b border-white/[0.04] hover:bg-white/[0.02] cursor-pointer"
                          onClick={() => setActiveTab('orders')} data-testid={`recent-order-${o.order_number}`}>
                          <td className="py-2.5 font-mono text-[#C6A15B]">{o.order_number}</td>
                          <td className="py-2.5 text-slate-200 truncate max-w-[160px]">{o.customer}</td>
                          <td className="py-2.5 text-slate-500 hidden sm:table-cell">{o.date ? new Date(o.date).toLocaleDateString() : '—'}</td>
                          <td className="py-2.5 text-right text-white font-medium">{money(o.total)}</td>
                          <td className="py-2.5 text-right">
                            <span className="inline-block text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${m.color}22`, color: m.color }}>{m.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-slate-500 text-sm py-8 text-center">No orders yet. Orders will appear here once created.</p>
          )}
        </Card>

        <Card className="p-5 lg:col-span-5" testid="top-products-card">
          <SectionTitle>Top Selling Products</SectionTitle>
          {loading ? <Skeleton className="h-[220px]" /> : (
            data?.top_products?.length ? (
              <div className="space-y-3">
                {data.top_products.map((p, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`top-product-${i}`}>
                    <span className="w-6 h-6 rounded-md bg-white/[0.05] text-slate-400 text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-200 text-sm truncate">{p.product_name}</p>
                      <p className="text-slate-500 text-xs">{p.units} units sold</p>
                    </div>
                    <span className="text-white font-medium text-sm shrink-0">{money(p.revenue)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-500 text-sm py-8 text-center">No sales data available for this period.</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
